import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import i18n from '../../i18n'

export interface TimerProps {
  minutesRemaining: number
  onComplete?: () => JSX.Element | undefined
  on10MinWarning?: () => JSX.Element | undefined
  on5MinWarning?: () => JSX.Element | undefined
  on1MinWarning?: () => JSX.Element | undefined
  on59secWarning?: () => JSX.Element | undefined
  on30secWarning?: () => JSX.Element | undefined
  on10secWarning?: () => JSX.Element | undefined
  on5secWarning?: () => JSX.Element | undefined
  on1secWarning?: () => JSX.Element | undefined
  onlyShowSeconds?: boolean
}

const CoundownTimer = ({
  minutesRemaining,
  onComplete = () => <Box component="span">{i18n.t('OutOfTime')}</Box>,
  on10MinWarning = () => undefined,
  on5MinWarning = () => undefined,
  on1MinWarning = () => undefined,
  on59secWarning = () => undefined,
  on30secWarning = () => undefined,
  on10secWarning = () => undefined,
  on5secWarning = () => undefined,
  on1secWarning = () => undefined,
  onlyShowSeconds = false,
}: TimerProps) => {
  const [secondsRemaining, setSeconds] = useState(minutesRemaining * 60)
  const [startTime] = useState(new Date())

  // Accounts for browser throttling that can occur on idle tabs
  // If timer is off by more than 5 seconds, adjusts to the actual time
  const checkTimeAccuracy = () => {
    const secondsElapsed = Math.round(
      (new Date().getTime() - startTime.getTime()) / 1000,
    )
    const ActualSecondsRemaining = minutesRemaining * 60 - secondsElapsed

    if (secondsRemaining - ActualSecondsRemaining > 5) {
      setSeconds(ActualSecondsRemaining)
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((seconds) => seconds - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  checkTimeAccuracy()

  const displayMinutes: string = Math.floor(secondsRemaining / 60)
    .toString()
    .padStart(2, '0')
  const displaySeconds: string = (secondsRemaining % 60)
    .toString()
    .padStart(2, '0')

  return (
    <Box className="Timer" role="timer">
      {secondsRemaining > 0 ? (
        <Box className="Time-Remaining">
          {!onlyShowSeconds || displayMinutes !== '00' ? displayMinutes : null}{' '}
          : {displaySeconds}{' '}
        </Box>
      ) : (
        <Box className="Time-Expired">
          <Box component="span">00 : 00</Box>
          <br />
          {onComplete ? onComplete() : null}
        </Box>
      )}
      {secondsRemaining < 601 && secondsRemaining > 300 && on10MinWarning
        ? on10MinWarning()
        : null}
      {secondsRemaining < 301 && secondsRemaining > 60 && on5MinWarning
        ? on5MinWarning()
        : null}
      {secondsRemaining < 61 && secondsRemaining > 30 && on1MinWarning
        ? on1MinWarning()
        : null}
      {secondsRemaining < 60 && secondsRemaining > 30 && on59secWarning
        ? on59secWarning()
        : null}
      {secondsRemaining < 31 && secondsRemaining > 10 && on30secWarning
        ? on30secWarning()
        : null}
      {secondsRemaining < 11 && secondsRemaining > 5 && on10secWarning
        ? on10secWarning()
        : null}
      {secondsRemaining < 6 && secondsRemaining > 0 && on5secWarning
        ? on5secWarning()
        : null}
      {secondsRemaining < 2 && secondsRemaining > 0 && on1secWarning
        ? on1secWarning()
        : null}
    </Box>
  )
}

export default CoundownTimer
